// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createAnalytics
 * @package @geenius/analytics/solidjs-css
 * @description Exposes the primary SolidJS analytics primitive for the CSS
 * variant. The primitive prefers provider context and falls back to a local
 * tracker when used in isolated component trees.
 */

import type {
  ConsentSettings,
  TrackerConfigInput,
  TrackerTrackOptions,
  UserTraits,
} from "@geenius/analytics";
import { createTracker } from "@geenius/analytics";
import { createSignal, useContext } from "solid-js";
import { AnalyticsCtx } from "../providers/AnalyticsProvider";

/**
 * Reactive analytics actions returned by {@link createAnalytics}.
 *
 * @property track Tracks a named analytics event with optional consent scope.
 * @property page Tracks a page view for a URL with optional properties.
 * @property identify Associates the current session with a user identity.
 * @property reset Clears the current analytics session state.
 * @property deleteUserData Deletes persisted analytics data for a known user.
 * @property forgetUser Alias for `deleteUserData` for right-to-forget flows.
 * @property consent Updates analytics consent settings for the tracker.
 * @property sessionId Signal accessor for the active tracker session identifier.
 */
export interface CreateAnalyticsReturn {
  track: (
    name: string,
    props?: Record<string, unknown>,
    options?: TrackerTrackOptions,
  ) => Promise<void>;
  page: (url: string, props?: Record<string, unknown>) => Promise<void>;
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
  deleteUserData: (userId: string) => Promise<void>;
  forgetUser: (userId: string) => Promise<void>;
  consent: (settings: ConsentSettings) => void;
  sessionId: () => string;
}

/**
 * Returns analytics actions for the current SolidJS CSS subtree.
 *
 * @param config Optional fallback tracker configuration used outside provider context.
 * @returns Analytics actions and a session-id accessor for the active tracker.
 *
 * @example
 * ```tsx
 * const analytics = createAnalytics()
 * void analytics.track('dashboard_opened')
 * ```
 */
export function createAnalytics(
  config?: TrackerConfigInput,
): CreateAnalyticsReturn {
  const contextTracker = useContext(AnalyticsCtx);
  const tracker = contextTracker ?? createTracker(config ?? {});
  const [sessionId, setSessionId] = createSignal(tracker.getSessionId());
  const reset = async (): Promise<void> => {
    await tracker.reset();
    setSessionId(tracker.getSessionId());
  };

  return {
    track: tracker.track,
    page: tracker.page,
    identify: tracker.identify,
    reset,
    deleteUserData: tracker.deleteUserData,
    forgetUser: tracker.forgetUser,
    consent: tracker.consent,
    sessionId,
  };
}
